/**
 * Bundle 141 - Email webhook audit page.
 *
 * Route: /admin/settings/email-webhooks (super_admin only).
 *
 * Read-only audit of inbound Resend webhook events. Bounces and
 * spam complaints upsert into `survey_opt_outs`; all events append
 * to `email_webhook_audit` regardless of handler outcome.
 *
 * Guardrails: no em/en dashes; no eslint-disable; no photo/image
 * in HTML alt attributes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { RefreshCw, Mail } from 'lucide-react';

const EVENT_TONE = {
  'email.bounced': 'bg-red-50 text-red-700 border-red-200',
  'email.complained': 'bg-amber-50 text-amber-700 border-amber-200',
  'email.opened': 'bg-neutral-50 text-neutral-700 border-neutral-200',
  'email.delivered': 'bg-green-50 text-green-700 border-green-200',
  'email.sent': 'bg-neutral-50 text-neutral-600 border-neutral-200',
  'email.clicked': 'bg-blue-50 text-blue-700 border-blue-200',
};

const AdminEmailWebhookAuditPage = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.event_type = filter;
      const { data } = await api.get(
        '/admin/settings/email-webhooks',
        { params });
      setRows(data?.rows || []);
      setTotals(data?.totals || {});
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Failed to load webhook audit');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      className="p-6 max-w-6xl mx-auto"
      data-testid="admin-email-webhook-audit-root"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary-700" />
            Email webhook receipts
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            Read-only audit of inbound Resend webhook events. Hard
            bounces and spam complaints automatically add the
            recipient to `survey_opt_outs` so future sends skip
            them.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="email-webhook-audit-refresh"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card data-testid="email-webhook-total-bounces">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-neutral-500 mb-1">
              Hard bounces
            </div>
            <div className="text-2xl font-bold text-red-700">
              {totals.bounces ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="email-webhook-total-complaints">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-neutral-500 mb-1">
              Spam complaints
            </div>
            <div className="text-2xl font-bold text-amber-700">
              {totals.complaints ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="email-webhook-total-optouts">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-neutral-500 mb-1">
              Opt-outs written by webhook
            </div>
            <div className="text-2xl font-bold text-neutral-800">
              {totals.opt_outs_from_webhook ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2 mb-3">
        {[
          ['all', 'All'],
          ['email.bounced', 'Bounces'],
          ['email.complained', 'Complaints'],
          ['email.opened', 'Opened'],
          ['email.delivered', 'Delivered'],
        ].map(([k, label]) => (
          <Button
            key={k}
            variant={filter === k ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(k)}
            data-testid={`email-webhook-filter-${k.replace('.', '-')}`}
          >
            {label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div
              className="p-6 text-sm text-neutral-500 text-center"
              data-testid="email-webhook-empty"
            >
              No webhook events recorded yet. Resend will populate
              this table once inbound webhooks arrive.
            </div>
          ) : (
            <table
              className="w-full text-sm"
              data-testid="email-webhook-audit-table"
            >
              <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-2">Received</th>
                  <th className="text-left px-4 py-2">Event</th>
                  <th className="text-left px-4 py-2">Recipient</th>
                  <th className="text-left px-4 py-2">Message id</th>
                  <th className="text-left px-4 py-2">Opt-out</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id || i}
                    className="border-t border-neutral-100"
                    data-testid="email-webhook-audit-row"
                  >
                    <td className="px-4 py-2 font-mono text-xs text-neutral-600">
                      {r.received_at}
                    </td>
                    <td className="px-4 py-2">
                      <Badge
                        variant="outline"
                        className={EVENT_TONE[r.event_type]
                          || 'bg-neutral-50 text-neutral-600'}
                      >
                        {r.event_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {r.recipient_email || 'unknown'}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-neutral-500">
                      {(r.resend_message_id || '').slice(0, 24)}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.opt_out_written ? (
                        <Badge className="bg-red-100 text-red-800">
                          {r.opt_out_reason}
                        </Badge>
                      ) : (
                        <span className="text-neutral-400">no</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmailWebhookAuditPage;
